/**
 * Public captain profile page mounted at `/captain/:id`.
 *
 * Fetches `GET /api/captains/:id` via `ApiClient`, then:
 *  - shows a centered loading spinner while pending (Req 1.6),
 *  - falls back to a placeholder image when the captain's photo is missing
 *    or fails to load (Req 1.9),
 *  - maps API 400/404 responses to a localized "الكابتن غير موجود"
 *    message (Reqs 1.2, 1.3),
 *  - shows a localized error message and a retry button on network or
 *    timeout failures (Req 1.7),
 *  - renders the support-phone footer notice (Req 1.4).
 *
 * Validates: Requirements 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9, 7.6
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type SyntheticEvent,
} from 'react';
import { useParams } from 'react-router-dom';

import { ApiClient, ApiError, type PublicCaptain } from '../api/ApiClient';
import { SUPPORT_EMAIL, SUPPORT_PHONE } from '../config/env';
import styles from './PublicCaptainPage.module.css';

// Placeholder SVG with navy brand background
const PLACEHOLDER_IMAGE_DATA_URI =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'>" +
      "<rect width='200' height='200' fill='#0f2044'/>" +
      "<circle cx='100' cy='80' r='40' fill='#1a3a6b'/>" +
      "<path d='M30 190c0-38 31-70 70-70s70 32 70 70' fill='#1a3a6b'/>" +
      '</svg>',
  );

const FETCH_TIMEOUT_MS = 10_000;

type Status = 'loading' | 'success' | 'not-found' | 'error';

function isNotFoundStatus(status: number): boolean {
  return status === 400 || status === 404;
}

export default function PublicCaptainPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [status, setStatus] = useState<Status>('loading');
  const [captain, setCaptain] = useState<PublicCaptain | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const requestIdRef = useRef(0);

  useEffect(() => {
    if (typeof id !== 'string' || id.length === 0) {
      setStatus('not-found');
      setCaptain(null);
      return;
    }

    const myRequestId = ++requestIdRef.current;
    setStatus('loading');
    setCaptain(null);
    setImageFailed(false);

    let settled = false;
    const timeoutHandle = window.setTimeout(() => {
      if (settled) return;
      if (requestIdRef.current !== myRequestId) return;
      settled = true;
      setStatus('error');
    }, FETCH_TIMEOUT_MS);

    ApiClient.getCaptain(id)
      .then((result) => {
        if (settled) return;
        if (requestIdRef.current !== myRequestId) return;
        settled = true;
        window.clearTimeout(timeoutHandle);
        setCaptain(result);
        setStatus('success');
      })
      .catch((err: unknown) => {
        if (settled) return;
        if (requestIdRef.current !== myRequestId) return;
        settled = true;
        window.clearTimeout(timeoutHandle);
        if (err instanceof ApiError && isNotFoundStatus(err.status)) {
          setStatus('not-found');
        } else {
          setStatus('error');
        }
      });

    return () => {
      window.clearTimeout(timeoutHandle);
      if (requestIdRef.current === myRequestId) {
        requestIdRef.current++;
      }
    };
  }, [id, attempt]);

  const handleRetry = useCallback(() => {
    setAttempt((n) => n + 1);
  }, []);

  const handleImageError = useCallback(
    (_e: SyntheticEvent<HTMLImageElement>) => {
      setImageFailed(true);
    },
    [],
  );

  return (
    <div className={styles.page} data-testid="public-captain-page">
      {/* Logo at top */}
      <header className={styles.logoHeader}>
        <img
          src="/logo.png"
          alt="وصل لي"
          className={styles.logoImg}
        />
      </header>
      {status === 'loading' && (
        <div className={styles.center}>
          <div
            className={styles.loadingBox}
            role="status"
            aria-live="polite"
          >
            <div className={styles.spinner} aria-hidden="true" />
            <p className={styles.loadingText}>جاري التحميل...</p>
          </div>
        </div>
      )}

      {status === 'not-found' && (
        <div className={styles.center}>
          <div className={styles.notFoundBox}>
            <span className={styles.notFoundIcon} aria-hidden="true">🪪</span>
            <p>الكابتن غير موجود</p>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className={styles.center}>
          <div className={styles.errorBox} role="alert">
            <p>تعذّر تحميل بيانات الكابتن، يرجى المحاولة لاحقاً</p>
            <button
              type="button"
              className={styles.retryButton}
              onClick={handleRetry}
            >
              إعادة المحاولة
            </button>
          </div>
        </div>
      )}

      {status === 'success' && captain !== null && (
        <div className={styles.center}>
          <article className={styles.card}>
            {/* Animated gradient photo border */}
            <div className={styles.photoWrapper}>
              <div className={styles.photoInner}>
                <img
                  className={styles.photo}
                  src={
                    imageFailed || !captain.imageUrl
                      ? PLACEHOLDER_IMAGE_DATA_URI
                      : captain.imageUrl
                  }
                  alt={captain.name}
                  onError={handleImageError}
                />
              </div>
            </div>

            <h1 className={styles.name}>{captain.name}</h1>
            <div className={styles.divider} aria-hidden="true" />
            <h2 className={styles.jobTitle}>{captain.jobTitle}</h2>

            <a
              className={styles.phoneLink}
              href={`tel:${captain.phoneNumber}`}
              aria-label={`اتصل بـ ${captain.name} على ${captain.phoneNumber}`}
            >
              <span className={styles.phoneIcon} aria-hidden="true">📞</span>
              {captain.phoneNumber}
            </a>
          </article>
        </div>
      )}

      <footer className={styles.footer}>
        <p className={styles.footerText}>في حال العثور على هذه البطاقة، يرجى التواصل معنا</p>
        <div className={styles.footerLinks}>
          {SUPPORT_PHONE && (
            <a
              className={styles.footerLink}
              href={`https://wa.me/${SUPPORT_PHONE.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="تواصل عبر واتساب"
            >
              <span className={styles.footerLinkIcon}>💬</span>
              واتساب
            </a>
          )}
          {SUPPORT_EMAIL && (
            <a
              className={styles.footerLink}
              href={`mailto:${SUPPORT_EMAIL}`}
              aria-label={`راسلنا على ${SUPPORT_EMAIL}`}
            >
              <span className={styles.footerLinkIcon}>✉️</span>
              {SUPPORT_EMAIL}
            </a>
          )}
        </div>
      </footer>
    </div>
  );
}
